// Physical paint state of one paint layer, stored as per-cell fields.
//
// Layers of the physical model, bottom to top:
//   surface   paper/canvas relief, absorbency (see surfaces/PaperModel.js)
//   dryRefl   spectral reflectance of everything that has dried into a permanent
//             film (paper colour + dried body paint). KM layering only needs the
//             substrate reflectance, so the dried stack collapses losslessly to it.
//   dryHeight relief of dried paint film
//   depPig    pigment settled onto/into the paper (re-wettable, liftable)
//   binderSet how firmly settled pigment is bound (0 fresh .. 1 set)
//   paperSat  water held in the paper's capillary layer
//   water     free surface water depth
//   wetPig    pigment suspended in the surface water
//   bodyVol   volume of wet paint body (pigment + binder vehicle)
//   bodyPig   pigment amount inside the wet body
//   bodyOpen  openness of the wet body (1 freshly wet .. 0 dried)
//   bodyMob/bodyYield/bodyDry/bodySolids/bodyGloss/depRewet: material properties
//             of the wet paint at this cell (volume-weighted when paints mix)
//
// Pigment identities live in a per-layer slot table of PIGMENT_SLOTS entries.

import { BAND_COUNT } from '../pigments/ColourConversion.js';
import { paperSpectrum } from '../pigments/PigmentLibrary.js';

export const PIGMENT_SLOTS = 8;
export const TILE_SIZE = 32;

const SCALAR_FIELDS = ['water', 'paperSat', 'binderSet', 'bodyVol', 'bodyOpen', 'bodyMob', 'bodyYield', 'bodyDry', 'bodySolids', 'bodyGloss', 'depRewet', 'dryHeight'];

export function createPaintState(width, height, surface, options = {}) {
  const n = width * height;
  const state = {
    width,
    height,
    surface,
    tilesX: Math.ceil(width / TILE_SIZE),
    tilesY: Math.ceil(height / TILE_SIZE),
    slots: new Array(PIGMENT_SLOTS).fill(null),
    wetPig: [],
    depPig: [],
    bodyPig: [],
    dryRefl: new Float32Array(n * BAND_COUNT),
    time: 0,
  };
  for (const key of SCALAR_FIELDS) state[key] = new Float32Array(n);
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    state.wetPig.push(new Float32Array(n));
    state.depPig.push(new Float32Array(n));
    state.bodyPig.push(new Float32Array(n));
  }
  const tileCount = state.tilesX * state.tilesY;
  state.activeTiles = new Uint8Array(tileCount);
  state.dirtyTiles = new Uint8Array(tileCount).fill(1);
  const paper = options.paperSpectrum || paperSpectrum(surface?.preset?.tone ?? 0.86, surface?.preset?.warmth ?? 0.04);
  for (let i = 0; i < n; i += 1) state.dryRefl.set(paper, i * BAND_COUNT);
  state.paperSpectrum = paper;
  return state;
}

export function scalarFieldNames() {
  return SCALAR_FIELDS.slice();
}

export function tileIndexOf(state, x, y) {
  return Math.floor(y / TILE_SIZE) * state.tilesX + Math.floor(x / TILE_SIZE);
}

export function markRect(state, x0, y0, x1, y1, active = true) {
  const tx0 = Math.max(0, Math.floor(x0 / TILE_SIZE));
  const ty0 = Math.max(0, Math.floor(y0 / TILE_SIZE));
  const tx1 = Math.min(state.tilesX - 1, Math.floor(x1 / TILE_SIZE));
  const ty1 = Math.min(state.tilesY - 1, Math.floor(y1 / TILE_SIZE));
  for (let ty = ty0; ty <= ty1; ty += 1) {
    for (let tx = tx0; tx <= tx1; tx += 1) {
      const t = ty * state.tilesX + tx;
      state.dirtyTiles[t] = 1;
      if (active) state.activeTiles[t] = 1;
    }
  }
}

// Finds or allocates the slot for a pigment. When all slots are taken, an
// empty slot (no pigment left anywhere) is reused; failing that, the pigment
// is mapped to the spectrally closest existing slot. That fallback is a known
// limitation of the fixed slot count (see PAINT_ENGINE_PROGRESS.md).
export function slotForPigment(state, pigment) {
  const existing = state.slots.findIndex((p) => p && p.id === pigment.id);
  if (existing >= 0) return existing;
  const free = state.slots.findIndex((p) => !p);
  if (free >= 0) {
    state.slots[free] = pigment;
    return free;
  }
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    if (totalPigmentInSlot(state, k) < 1e-6) {
      state.slots[k] = pigment;
      return k;
    }
  }
  let best = 0;
  let bestDistance = Infinity;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    const other = state.slots[k];
    let d = 0;
    for (let band = 0; band < BAND_COUNT; band += 1) {
      d += (Math.log1p(other.K[band] / other.S[band]) - Math.log1p(pigment.K[band] / pigment.S[band])) ** 2;
    }
    if (d < bestDistance) {
      bestDistance = d;
      best = k;
    }
  }
  return best;
}

export function totalPigmentInSlot(state, k) {
  let sum = 0;
  const a = state.wetPig[k];
  const b = state.depPig[k];
  const c = state.bodyPig[k];
  for (let i = 0; i < a.length; i += 1) sum += a[i] + b[i] + c[i];
  return sum;
}

// Totals used by conservation tests and the benchmark report.
export function measureTotals(state) {
  let water = 0;
  let paperWater = 0;
  let body = 0;
  let pigment = 0;
  let dryHeight = 0;
  let nonFinite = 0;
  const n = state.width * state.height;
  for (let i = 0; i < n; i += 1) {
    water += state.water[i];
    paperWater += state.paperSat[i];
    body += state.bodyVol[i];
    dryHeight += state.dryHeight[i];
    if (!Number.isFinite(state.water[i]) || !Number.isFinite(state.bodyVol[i]) || !Number.isFinite(state.paperSat[i])) nonFinite += 1;
  }
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    for (let i = 0; i < n; i += 1) {
      const v = state.wetPig[k][i] + state.depPig[k][i] + state.bodyPig[k][i];
      if (!Number.isFinite(v)) nonFinite += 1;
      pigment += v;
    }
  }
  return { water, paperWater, body, pigment, dryHeight, nonFinite };
}
